import { yupResolver } from "@hookform/resolvers/yup";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { Alert } from "Components/_Shared/Alert";
import { Modal } from "Components/_Shared/BlModal";
import { BlTooltip } from "Components/_Shared/BlTooltip";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { type ResourcePathType, Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { type ObjectSchema, object, string } from "yup";
import { ANALYSIS_NOTE_MAX_LENGTH } from "../constants";
import { AnalysisDetailContext } from "./AnalysisDetailContext";

type Props = {
  readonly content: string;
  readonly onSave: (content: string) => void;
  readonly readOnly?: boolean;
};

type InternalNoteFormType = {
  note?: string;
};

const getInternalNoteSchema: (t: (resourcePath: ResourcePathType, options?: any) => string) => ObjectSchema<InternalNoteFormType> = (t) => {
  return object({
    note: string().max(ANALYSIS_NOTE_MAX_LENGTH, t(Resources.Validation.String.Max, { count: ANALYSIS_NOTE_MAX_LENGTH })),
  }).defined();
};

export const InternalNoteModal = ({ content, onSave }: Props) => {
  const { t } = useResource();
  const { readOnly } = useContext(AnalysisDetailContext);
  const isAnalysisSharingEnabled = useFeatureFlag(FeatureFlag.AnalysisSharing);

  const [open, setOpen] = useState(false);

  const methods = useForm<InternalNoteFormType>({
    resolver: yupResolver(getInternalNoteSchema(t)),
    defaultValues: {
      note: content,
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (open) {
      reset({ note: content });
    }
  }, [open, content, reset]);

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = (data: InternalNoteFormType) => {
    onSave(data.note ?? "");
    setOpen(false);
  };

  return (
    <>
      <BlTooltip title={t(Resources.AnalysisDetail.Note.Tooltip)}>
        <IconButton onClick={() => setOpen(true)}>
          <StickyNote2OutlinedIcon fontSize="small" sx={{ color: "text.primary" }} />
        </IconButton>
      </BlTooltip>
      <Modal open={open} onClose={handleClose}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex sx={{ boxShadow: 3, background: "white", p: Spacing.XXL, borderRadius: Radius.XXXL }}>
              <Stack spacing={Spacing.XXL}>
                <Flex justifyContent="space-between">
                  <Typography variant="h2">{t(Resources.AnalysisDetail.Note.Title)}</Typography>
                  <IconButton onClick={handleClose}>
                    <CloseIcon />
                  </IconButton>
                </Flex>
                <Flex sx={{ width: "100%" }}>
                  <Alert
                    variant="info"
                    text={t(isAnalysisSharingEnabled ? Resources.AnalysisDetail.Note.SharedHint : Resources.AnalysisDetail.Note.Hint)}
                  />
                </Flex>

                <BlFormInput
                  disabled={readOnly}
                  control={control}
                  label={t(Resources.AnalysisDetail.Note.Title)}
                  name="note"
                  errors={errors}
                  multiline
                  minRows={6}
                  maxRows={8}
                  characterCounter={<FormCharacterCounter characterLimit={ANALYSIS_NOTE_MAX_LENGTH} name={"note"} />}
                />
                <Box display="flex" justifyContent="flex-end">
                  <Box mr={Spacing.XL}>
                    <Button color="secondary" onClick={handleClose}>
                      {t(Resources.Common.Close)}
                    </Button>
                  </Box>
                  <Button disabled={readOnly || !!errors.note} type="submit">
                    {t(Resources.Common.Save)}
                  </Button>
                </Box>
              </Stack>
            </Flex>
          </form>
        </FormProvider>
      </Modal>
    </>
  );
};
